import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Bert } from 'meteor/themeteorchef:bert';
import { cloneDeep, find, includes, indexOf, isEmpty, isEqual, map } from 'lodash';
import { Button, Row, Col, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import turf from '@turf/turf';

import * as annotationOps from '../../../api/Annotations/annotation-operations';
import * as templateOps from '../../../api/AnnotationTemplates/annotation-template-operations';
import { CAPTION_TYPES, ELEMENT_TYPES } from '../../../api/AnnotationTemplates/AnnotationTemplates';

import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import ViewAnnotationTemplate from '../../pages/ViewAnnotationTemplate';

class AnnotationEditor extends Component {
  state = {
    updatedAnnotation: {
      _id: annotationOps.getId(this.props.annotation),
      geoJSON: {
        type: 'Feature',
        geometry: turf.getGeom(annotationOps.getGeoJSON(this.props.annotation)),
        properties: {
          videoTime: annotationOps.getVideoTime(this.props.annotation),
          videoPosition: annotationOps.getVideoPosition(this.props.annotation),
          template: annotationOps.getTemplate(this.props.annotation)
        }
      },
      origin: annotationOps.getOrigin(this.props.annotation)
    },
    selectedTemplate: templateOps.getId(annotationOps.getTemplate(this.props.annotation))
  };

  handleCancel = () => {
    const { annotation, changeVideoContainerStateProperty, toggleButtonSwitch } = this.props;
    const { _id: existingAnnotation } = annotation;
    changeVideoContainerStateProperty('readOnly', true);
    if (!existingAnnotation) {
      changeVideoContainerStateProperty('selectedAnnotation', {});
      toggleButtonSwitch();
    }
  };

  handleSelectedTemplateChange = newSelectedTemplate => {
    const { annotationTemplates } = this.props;

    const selectedAnnotationTemplate = find(annotationTemplates, template =>
      isEqual(templateOps.getId(template), newSelectedTemplate)
    );

    const newAnnotationTemplate = cloneDeep(selectedAnnotationTemplate);

    const newAnnotationTemplateElements = map(
      templateOps.getElements(newAnnotationTemplate),
      element => {
        const newElement = cloneDeep(element);
        newElement.value = '';
        if (includes(CAPTION_TYPES, templateOps.getElementType(element))) newElement.caption = '';
        if (templateOps.getElementType(element) === ELEMENT_TYPES.ICON) newElement.iconKey = '';
        return newElement;
      }
    );
    newAnnotationTemplate.elements = newAnnotationTemplateElements;

    this.setState(prevState => {
      const newAnnotation = { ...prevState.updatedAnnotation };
      newAnnotation.geoJSON.properties.template = newAnnotationTemplate;
      return { updatedAnnotation: newAnnotation, selectedTemplate: newSelectedTemplate };
    });
  };

  handleSubmit = async () => {
    const {
      annotation,
      videoId,
      changeVideoContainerStateProperty,
      toggleButtonSwitch
    } = this.props;
    const { _id: existingAnnotation } = annotation;
    const { updatedAnnotation } = this.state;

    try {
      const template = annotationOps.getTemplate(updatedAnnotation);
      if (isEmpty(template)) throw new Error('No annotation template selected.');

      const annotationToSave = {
        geoJSON: {
          type: 'Feature',
          geometry: turf.getGeom(updatedAnnotation.geoJSON),
          properties: {
            video: videoId,
            videoTime: annotationOps.getVideoTime(updatedAnnotation),
            videoPosition: annotationOps.getVideoPosition(updatedAnnotation),
            template
          }
        },
        origin: annotationOps.getOrigin(updatedAnnotation)
      };

      const methodToCall = existingAnnotation ? 'annotations.update' : 'annotations.insert';
      const bertMessage = existingAnnotation ? 'Annotation updated' : 'Annotation created';
      const buttonToToggle = existingAnnotation ? '' : 'addAnnotationButton';

      if (existingAnnotation) annotationToSave._id = annotationOps.getId(annotation);
      const annotationId = await methodPromise(methodToCall, annotationToSave);
      const savedAnnotation = { _id: annotationId, ...annotationToSave };

      changeVideoContainerStateProperty('readOnly', true);
      changeVideoContainerStateProperty('selectedAnnotation', savedAnnotation);

      toggleButtonSwitch(buttonToToggle);
      Bert.alert(bertMessage, 'success');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  updateAnnotationFields = (elementItem, property, newValue) =>
    this.setState(prevState => {
      const newAnnotation = { ...prevState.updatedAnnotation };
      const templateElementsArray = templateOps.getElements(
        annotationOps.getTemplate(newAnnotation)
      );
      const elementToChange = find(templateElementsArray, element =>
        isEqual(
          indexOf(templateElementsArray, element),
          indexOf(templateElementsArray, elementItem)
        )
      );
      elementToChange[property] = newValue;
      return { updatedAnnotation: newAnnotation };
    });

  render() {
    const { annotationTemplates } = this.props;
    const { updatedAnnotation, selectedTemplate } = this.state;

    const saveButtonText = annotationOps.getId(updatedAnnotation)
      ? 'Save Changes'
      : 'Add Annotation';

    return (
      <Row className="pl-3">
        <Col xs={12} className="px-0 overflow-auto">
          <ButtonGroup size="sm">
            {map(annotationTemplates, (template, index) => {
              const buttonStyle = isEqual(templateOps.getId(template), selectedTemplate)
                ? 'success'
                : 'light';
              return (
                <Button
                  key={`${templateOps.getName(template)}-${index}`}
                  variant={buttonStyle}
                  onClick={() => this.handleSelectedTemplateChange(templateOps.getId(template))}
                >
                  <FontAwesomeIcon icon={templateOps.getIcon(template)} size="lg" />
                </Button>
              );
            })}
          </ButtonGroup>
        </Col>
        <Col xs={12}>
          <ViewAnnotationTemplate
            selectedTemplate={selectedTemplate}
            annotationTemplate={annotationOps.getTemplate(updatedAnnotation)}
            updateAnnotationFields={this.updateAnnotationFields}
            readOnly
            startingPreviewMode="edition"
            {...this.state}
          />
        </Col>
        <Col xs={12} className="d-flex justify-content-end">
          <Button variant="danger" onClick={this.handleCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="annotationTemplatePreviewForm"
            variant="success"
            onClick={this.handleSubmit}
          >
            {saveButtonText}
          </Button>
        </Col>
      </Row>
    );
  }
}

AnnotationEditor.defaultProps = {};

AnnotationEditor.propTypes = {
  annotation: PropTypes.object.isRequired,
  annotationTemplates: PropTypes.array.isRequired,
  videoId: PropTypes.string.isRequired,
  changeVideoContainerStateProperty: PropTypes.func.isRequired,
  toggleButtonSwitch: PropTypes.func.isRequired
};

export default AnnotationEditor;
